import React, { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import firebase from '../store/api/fbConfig';

const initialState = {
  firebase: {
    auth: undefined,
    profile: undefined,
  },
  firestore: {
    ordered: {},
    data: {},
  },
};

let state = initialState;
const listeners = new Set();
const activeSubscriptions = new Map();

const notify = () => {
  for (const listener of listeners) listener();
};

const setState = updater => {
  const next = typeof updater === 'function' ? updater(state) : updater;
  state = next;
  notify();
};

export const getState = () => state;

export const subscribe = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const setAuthUser = user => {
  setState(prev => ({
    ...prev,
    firebase: {
      ...prev.firebase,
      auth: user || null,
    },
  }));
};

export const startAuthListener = () => firebase.auth().onAuthStateChanged(setAuthUser);

const serialiseConfig = config => JSON.stringify(config, (_key, value) => {
  if (typeof value === 'function') return '[function]';
  return value;
});

const normaliseOrderBy = query => {
  if (!query) return [];
  if (Array.isArray(query) && Array.isArray(query[0])) return query;
  if (Array.isArray(query)) return [query];
  if (typeof query === 'string') return [[query, 'asc']];
  return [];
};

const applyQueryOptions = (ref, config = {}) => {
  let query = ref;

  const where = Array.isArray(config.where) ? config.where : [];
  for (const condition of where) {
    if (Array.isArray(condition) && condition.length >= 3) {
      query = query.where(condition[0], condition[1], condition[2]);
    }
  }

  for (const order of normaliseOrderBy(config.orderBy)) {
    if (Array.isArray(order) && order.length >= 2) query = query.orderBy(order[0], order[1]);
    else if (Array.isArray(order) && order.length === 1) query = query.orderBy(order[0]);
  }

  if (config.limit) query = query.limit(config.limit);

  return query;
};

const getRefFromConfig = config => {
  if (!config || !config.collection) return null;

  let ref = firebase.firestore().collection(config.collection);

  if (config.doc) {
    ref = ref.doc(config.doc);

    if (Array.isArray(config.subcollections) && config.subcollections.length > 0) {
      const [subcollection] = config.subcollections;
      ref = ref.collection(subcollection.collection);
      return applyQueryOptions(ref, subcollection);
    }

    return ref;
  }

  return applyQueryOptions(ref, config);
};

const setOrderedValue = (storeAs, value) => {
  setState(prev => ({
    ...prev,
    firestore: {
      ...prev.firestore,
      ordered: {
        ...prev.firestore.ordered,
        [storeAs]: value,
      },
    },
  }));
};

const snapshotToArray = snapshot => {
  if (!snapshot) return [];
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

const snapshotToDocArray = snapshot => {
  if (!snapshot || !snapshot.exists) return [];
  return [{ id: snapshot.id, ...snapshot.data() }];
};

const subscribeFirestoreConfig = config => {
  if (!config || !config.collection) return () => {};

  const storeAs = config.storeAs || config.collection;
  const key = serialiseConfig({ ...config, storeAs });

  if (activeSubscriptions.has(key)) {
    const active = activeSubscriptions.get(key);
    active.count += 1;
    return () => {
      active.count -= 1;
      if (active.count <= 0) {
        active.unsubscribe();
        activeSubscriptions.delete(key);
      }
    };
  }

  const ref = getRefFromConfig(config);
  if (!ref || typeof ref.onSnapshot !== 'function') return () => {};

  setOrderedValue(storeAs, undefined);

  const unsubscribe = ref.onSnapshot(snapshot => {
    const value = snapshot.docs ? snapshotToArray(snapshot) : snapshotToDocArray(snapshot);
    setOrderedValue(storeAs, value);
  }, error => {
    console.error('Firestore subscription failed', { storeAs, config, error });
    setOrderedValue(storeAs, []);
  });

  activeSubscriptions.set(key, { count: 1, unsubscribe });

  return () => {
    const active = activeSubscriptions.get(key);
    if (!active) return;
    active.count -= 1;
    if (active.count <= 0) {
      active.unsubscribe();
      activeSubscriptions.delete(key);
    }
  };
};

export const resolveFirestoreConfigs = (configs, props = {}) => {
  const resolved = typeof configs === 'function' ? configs(props) : configs;
  if (!resolved) return [];
  return Array.isArray(resolved) ? resolved.filter(Boolean) : [resolved];
};

export const useFirestoreSubscriptions = (configs, props = {}) => {
  const resolved = useMemo(() => resolveFirestoreConfigs(configs, props), [configs, props]);
  const key = useMemo(() => serialiseConfig(resolved), [resolved]);

  useEffect(() => {
    const unsubscribers = resolved.map(subscribeFirestoreConfig);
    return () => unsubscribers.forEach(unsubscribe => unsubscribe());
  }, [key]);
};

export const useStoreSelector = (selector, dependencies = []) => {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const currentState = useSyncExternalStore(
    subscribe,
    getState,
    () => initialState,
  );

  return useMemo(
    () => selectorRef.current(currentState),
    [currentState, ...dependencies],
  );
};

export const isLoaded = (...values) => values.every(value => value !== undefined);

export const isEmpty = value => {
  if (value === undefined) return false;
  if (value === null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return !value;
};
